import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from "react";
import { trackSrc, api } from "@/lib/api";

const PlayerContext = createContext(null);
const COARSE = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)")?.matches;
const withCount = (url, count) => (count ? url : url + (url.includes("?") ? "&" : "?") + "count=false");

export function PlayerProvider({ children }) {
  // Two reusable <audio> elements: one playing, one pre-buffering the most likely next track.
  const activeRef = useRef(null);
  const standbyRef = useRef(null);
  const [current, setCurrent] = useState(null);
  const [queue, setQueue] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);
  const currentRef = useRef(null);
  const queueRef = useRef([]);
  const volumeRef = useRef(0.85);
  const retriesRef = useRef(0);
  const hoverTimer = useRef(null);
  useEffect(() => { currentRef.current = current; }, [current]);
  useEffect(() => { queueRef.current = queue; }, [queue]);

  useEffect(() => {
    const make = () => {
      const a = new Audio();
      a.preload = "auto";
      a.volume = volumeRef.current;
      a.addEventListener("timeupdate", () => { if (a === activeRef.current) setProgress(a.currentTime || 0); });
      a.addEventListener("loadedmetadata", () => { if (a === activeRef.current) setDuration(a.duration || 0); });
      a.addEventListener("durationchange", () => { if (a === activeRef.current) setDuration(a.duration || 0); });
      a.addEventListener("playing", () => { if (a === activeRef.current) setIsPlaying(true); });
      a.addEventListener("pause", () => { if (a === activeRef.current) setIsPlaying(false); });
      a.addEventListener("ended", () => { if (a === activeRef.current) { setIsPlaying(false); advance(); } });
      a.addEventListener("error", () => {
        if (a !== activeRef.current) return;
        const cur = currentRef.current;
        if (!cur || retriesRef.current >= 2) { setIsPlaying(false); return; }
        retriesRef.current += 1;
        const wasAt = a.currentTime || 0;
        setTimeout(() => {
          if (currentRef.current?.id !== cur.id || a !== activeRef.current) return;
          const base = trackSrc(cur);
          a.src = base + (base.includes("?") ? "&" : "?") + `retry=${retriesRef.current}`;
          a.dataset.url = "";
          a.currentTime = wasAt;
          a.play().catch(() => setIsPlaying(false));
        }, 800 * retriesRef.current);
      });
      return a;
    };
    activeRef.current = make();
    standbyRef.current = make();
    window.__pomataAudio = () => activeRef.current;
    return () => { activeRef.current?.pause(); standbyRef.current?.pause(); };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    volumeRef.current = volume;
    if (activeRef.current) activeRef.current.volume = volume;
    if (standbyRef.current) standbyRef.current.volume = volume;
  }, [volume]);

  // Pre-buffer a track into the standby element (metadata = light, auto = full buffer)
  const prefetchNow = useCallback((track, mode = "metadata") => {
    const s = standbyRef.current, a = activeRef.current;
    if (!track || !s || !a) return;
    const url = trackSrc(track);
    if (a.dataset.url === url) return;
    if (s.dataset.url === url) { if (mode === "auto" && s.preload !== "auto") { s.preload = "auto"; s.load(); } return; }
    s.pause();
    s.preload = mode;
    s.src = withCount(url, false);
    s.dataset.url = url;
    s.load();
  }, []);

  // Hover/intent prefetch: debounced so sweeping the mouse over a list does not spam requests
  const prefetch = useCallback((track) => {
    if (!track || COARSE) return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => prefetchNow(track, "metadata"), 180);
  }, [prefetchNow]);

  const playTrackInternal = (track, newQueue = []) => {
    if (!track || !activeRef.current) return;
    const url = trackSrc(track);
    let a = activeRef.current;
    const s = standbyRef.current;
    retriesRef.current = 0;
    if (s && s.dataset.url === url) {
      // Swap: the pre-buffered element becomes the player → no new connection needed
      a.pause();
      activeRef.current = s;
      standbyRef.current = a;
      a = s;
      a.preload = "auto";
      if (a.currentTime) a.currentTime = 0;
      // the standby request was made with count=false → register the play explicitly
      const m = /\/stream\/track\/([^/?]+)(\?|$)/.exec(url);
      if (m) api.post(`/tracks/${m[1]}/play`).catch(() => {});
    } else if (a.dataset.url !== url) {
      a.src = url;
      a.dataset.url = url;
    } else {
      a.currentTime = 0;
    }
    a.volume = volumeRef.current;
    setProgress(0);
    setDuration(a.duration || track.duration || 0);
    a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    setCurrent(track);
    if (newQueue.length) setQueue(newQueue);
    if (newQueue[0]) setTimeout(() => prefetchNow(newQueue[0], COARSE ? "metadata" : "auto"), 1500);
  };

  const advance = () => {
    if (queueRef.current.length > 0) {
      const [n, ...rest] = queueRef.current;
      setQueue(rest);
      playTrackInternal(n, rest);
    }
  };

  const playTrack = useCallback((track, newQueue = []) => {
    if (!track || !activeRef.current) return;
    const a = activeRef.current;
    if (currentRef.current?.id === track.id) {
      if (a.paused) { a.play().catch(() => {}); setIsPlaying(true); } else { a.pause(); setIsPlaying(false); }
      return;
    }
    playTrackInternal(track, newQueue);
    // eslint-disable-next-line
  }, []);

  const togglePlay = useCallback(() => {
    const a = activeRef.current;
    if (!a || !currentRef.current) return;
    if (a.paused) { a.play().catch(() => {}); setIsPlaying(true); } else { a.pause(); setIsPlaying(false); }
  }, []);

  const seek = useCallback((t) => {
    if (!activeRef.current || !Number.isFinite(t) || t < 0) return;
    activeRef.current.currentTime = t;
  }, []);

  const next = useCallback(() => advance(), []); // eslint-disable-line

  return (
    <PlayerContext.Provider value={{
      current, isPlaying, progress, duration, volume,
      setVolume, playTrack, togglePlay, seek, next, prefetch, prefetchNow,
      previewSec: null,
    }}>
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
